import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { setThemePref, useTheme, type ThemePref } from "@/shared/theme/useTheme";

/**
 * Three-way theme control (light / system / dark) — segmented pill, same
 * rhythm as the seller portal's segmented controls.
 */
export function ThemeToggle() {
  const t = useTranslations();
  const { pref } = useTheme();

  const options: { value: ThemePref; label: string; icon: React.ReactNode }[] = [
    { value: "light", label: t(keys.common.themeLight), icon: <SunIcon /> },
    { value: "system", label: t(keys.common.themeSystem), icon: <MonitorIcon /> },
    { value: "dark", label: t(keys.common.themeDark), icon: <MoonIcon /> },
  ];

  return (
    <div
      role="radiogroup"
      aria-label={t(keys.common.themeTitle)}
      className="flex w-full rounded-[10px] border border-[color:var(--line)] bg-[color:var(--surface-alt)] p-[3px]"
    >
      {options.map((option) => {
        const isActive = pref === option.value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={isActive}
            aria-label={option.label}
            title={option.label}
            onClick={() => setThemePref(option.value)}
            className={[
              "flex h-8 flex-1 items-center justify-center rounded-[8px] transition-colors",
              isActive
                ? "bg-[color:var(--surface)] text-[color:var(--text-primary)] shadow-[0_1px_2px_rgba(0,0,0,0.06)]"
                : "text-[color:var(--text-muted)] hover:text-[color:var(--text-secondary)]",
            ].join(" ")}
          >
            {option.icon}
          </button>
        );
      })}
    </div>
  );
}

const ICON = {
  width: 16,
  height: 16,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor" as const,
  strokeWidth: 1.8,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

function SunIcon() {
  return (
    <svg {...ICON}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </svg>
  );
}

function MonitorIcon() {
  return (
    <svg {...ICON}>
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8" />
      <path d="M12 17v4" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg {...ICON}>
      <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
    </svg>
  );
}
